import React, { useContext } from "react";
import { Field } from "formik";
import { Form as FormBase } from "react-bootstrap";
import {
  FormControlLabel as FormControlLabelBase,
  Checkbox,
  InputLabel,
  styled,
} from "@mui/material";

import GenericFieldArray from "./GenericFieldArray";
import { toastr } from "react-redux-toastr";
import FormikMUIAutocompleteSelectInput from "@/components/muiCustom/select/FormikMUIAutocompleteSelectInput";
import RpMuiSelect from "@/components/muiCustom/select/RpMuiSelect";
import { RpDropzone } from "@/components/fileUploadInput/RpDropzone";
import NumberRangeMDInputFormik from "@/components/muiCustom/number/NumberRangeMDInputFormik";
import RpDatePicker from "@/components/muiCustom/date/RpDatePicker";
import {
  emailMDInputFormik,
  numberMDInputFormik,
  textMDInputFormik,
} from "@/helpers/formInputs";
import MUIDateRangeBase from "@/components/MUIDateRange";
import DownloadLinkButtonBase from "@/components/muiCustom/buttons/DownloadLinkButton";
import { getTemplateUrl as getTemplateUrlBase } from "../utils/formConfigs";
import { GlobalContext } from "@/helpers/contexts/contexts";
import { BRAND_REQUEST_TYPE } from "@/config/constants";

type SelectOption = {
  label?: string;
  value: string | number;
  [key: string]: unknown;
};

type DependentField = {
  field: string;
  clearValue: unknown;
  whenChecked: boolean;
};

type DateRangeValue = {
  startDate?: unknown;
  endDate?: unknown;
};

type FormFieldProps = {
  type?: string;
  name: string;
  label?: string;
  options?: SelectOption[];
  requestType?: string;
  dependentFields?: DependentField[];
  fieldType?: string;
  maxFields?: number;
  required?: boolean;
  multiple?: boolean;
  selectAll?: boolean;
  className?: string;
  showTemplate?: boolean;
  templateConfig?: Record<string, unknown>;
  secondaryTemplateConfig?: Record<string, unknown>;
  disablePast?: boolean;
  [key: string]: any;
};

type SelectedBrand = {
  id?: string | number;
};

const getTemplateUrl = getTemplateUrlBase as (
  _templateConfig?: Record<string, unknown>,
  _id?: string | number,
  _dateRange?: DateRangeValue
) => Promise<string>;

const FormGroup = FormBase.Group as React.ComponentType<
  Record<string, unknown>
>;
const FormLabel = FormBase.Label as React.ComponentType<
  Record<string, unknown>
>;
const FormCheck = FormBase.Check as React.ComponentType<
  Record<string, unknown>
>;
const FormFeedback = FormBase.Control.Feedback as React.ComponentType<
  Record<string, unknown>
>;
const MUIDateRange = MUIDateRangeBase as React.ComponentType<
  Record<string, unknown>
>;
const FormControlLabel = FormControlLabelBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const DownloadLinkButton = DownloadLinkButtonBase as React.ComponentType<
  Record<string, unknown>
>;

// ---------------- Styled Components ----------------
const StyledInputLabel = styled(InputLabel)(() => ({
  color: "var(--neutral-700)",
  fontSize: ".92rem",
  fontWeight: 700,
  margin: 0,
  padding: "4px 0px 0px 10px",
}));

const inputWidthSx = {
  maxWidth: "1500px",
  margin: 0,
  padding: 0,
};

const dateRangeInputSx = {
  color: "var(--neutral-700)",
  backgroundColor: "var(--neutral-white)",
  padding: "6px 14px",
  borderRadius: "var(--radius-control)",
  marginTop: "8px",
  marginBottom: "10px",
  border: "1px solid var(--neutral-500)",
  "&:hover": {
    border: "1px solid var(--neutral-600)",
  },
  "&:focus-within": {
    border: "2px solid var(--primary-base)",
    padding: "7px 13px",
  },
};

const dateRangeTextSx = {
  fontSize: "14px",
  color: "var(--neutral-700)",
};

const dateRangeIconSx = {
  color: "var(--neutral-500)",
  "& .MuiSvgIcon-root": {
    color: "var(--neutral-500)",
  },
};

// ---------------- Main Component ----------------
const FormField = ({
  type,
  name,
  label,
  options,
  requestType = "",
  ...props
}: FormFieldProps) => {
  const { selectedBrand } = useContext(GlobalContext);
  const brand = selectedBrand as SelectedBrand | null | undefined;
  const selectOptions = options || [];

  if (!type) return null;

  switch (type) {
    case "dropdownWithAll":
      return (
        <>
          <StyledInputLabel shrink margin="dense" required={props?.required}>
            {label}
          </StyledInputLabel>
          <Field name={name}>
            {({ field, form }) => {
              const hasError =
                form.touched[field.name] && form.errors[field.name];
              const isGroupSelected =
                form.values.fk_group_id && form.values.fk_group_id.length > 0;

              return (
                <>
                  <RpMuiSelect
                    options={selectOptions}
                    multiple={props?.multiple}
                    value={field.value || (props?.multiple ? [] : "")}
                    onChange={(selectedValues) => {
                      if (
                        name === "fk_location_id" &&
                        !isGroupSelected &&
                        selectedValues.length > 0 &&
                        requestType === BRAND_REQUEST_TYPE.NEW_CAMPAIGN
                      ) {
                        toastr.error("Error", "Please Select a Group First", {
                          id: "toast-error",
                          timeOut: 3000,
                        });
                        return;
                      }

                      if (props?.selectAll && selectedValues.includes("all")) {
                        form.setFieldValue(
                          field.name,
                          selectOptions.map((opt) => opt.value)
                        );
                      } else {
                        form.setFieldValue(field.name, selectedValues);
                      }
                    }}
                    onFocus={() => {
                      if (
                        name === "fk_location_id" &&
                        !isGroupSelected &&
                        requestType === BRAND_REQUEST_TYPE.NEW_CAMPAIGN
                      ) {
                        toastr.error("Error", "Please Select a Group First", {
                          id: "toast-error",
                          timeOut: 3000,
                        });
                      }
                    }}
                    placeholder={field.placeholder ? field.placeholder : ""}
                    size="small"
                    {...props}
                    required={props.required}
                    error={hasError}
                    className={`w-100 px-2 pr-0 mb-1 p-0 mt-2`}
                    disableCloseOnSelect={false}
                  />
                  {hasError && (
                    <div className="text-danger px-2 mb-3 fs-12 fw-sb">
                      {form.errors[field.name]}
                    </div>
                  )}
                </>
              );
            }}
          </Field>
        </>
      );

    case "dropdown":
      return (
        <>
          <StyledInputLabel shrink margin="dense" required={props?.required}>
            {label}
          </StyledInputLabel>
          <Field name={name}>
            {(fieldProps) => (
              <FormikMUIAutocompleteSelectInput
                options={selectOptions}
                multiple={
                  ![
                    "campaign_type",
                    "payment_method",
                    "fk_campaign_id",
                    "campaign_goal",
                  ].includes(name)
                }
                size="small"
                {...fieldProps}
                {...props}
                className={`w-100 px-2 pr-0 p-0 m-0 ${props?.className || ""}`}
                disableCloseOnSelect={
                  ![
                    "campaign_type",
                    "payment_method",
                    "fk_campaign_id",
                    "campaign_goal",
                  ].includes(name)
                }
              />
            )}
          </Field>
        </>
      );

    case "file":
      return (
        <Field name={name}>
          {({ field, form }) => (
            <RpDropzone
              field={field}
              form={form}
              label={label}
              multiple={field?.multiple || false}
              maxRequiredSize={field?.maxRequiredSize || 150 * 1024 * 1024}
              {...props}
              className="w-100 px-2 py-2 pr-0 p-0"
              downloadTemplate={
                <>
                  {props?.showTemplate && props?.templateConfig && (
                    <DownloadLinkButton
                      downloadLink={props.templateConfig.downloadLink}
                      getDownloadUrl={() =>
                        getTemplateUrl(
                          props?.templateConfig,
                          brand?.id,
                          form?.values?.budgetDateRange
                        )
                      }
                      buttonText={
                        props?.templateConfig?.buttonText || "Download Template"
                      }
                      variant="outlined"
                      customStyle={{ fontSize: ".62rem" }}
                    />
                  )}
                  {props?.showTemplate && props?.secondaryTemplateConfig && (
                    <DownloadLinkButton
                      downloadLink={props.secondaryTemplateConfig.downloadLink}
                      getDownloadUrl={() =>
                        getTemplateUrl(props?.secondaryTemplateConfig)
                      }
                      buttonText={
                        props?.secondaryTemplateConfig?.buttonText ||
                        "Download Template"
                      }
                      variant="outlined"
                      className="ml-3"
                      customStyle={{ fontSize: ".62rem" }}
                    />
                  )}
                </>
              }
            />
          )}
        </Field>
      );

    case "date":
      return (
        <Field name={name}>
          {({ field, form }) => {
            const hasError =
              (form.touched[field.name] || form.submitCount > 0) &&
              form.errors[field.name];
            const { className, ...dateProps } = props;

            return (
              <>
                <StyledInputLabel
                  shrink
                  margin="dense"
                  className="mb-2"
                  required={props?.required}
                >
                  {label}
                </StyledInputLabel>
                <RpDatePicker
                  {...dateProps}
                  name={field.name}
                  value={field.value || null}
                  onChange={(value) => {
                    const formattedValue = value?.format("YYYY-MM-DD") || null;
                    form.setFieldTouched(field.name, true, false);
                    form.setFieldValue(field.name, formattedValue, true);
                    form.validateField(field.name);
                  }}
                  onBlur={() => {
                    form.setFieldTouched(field.name, true, false);
                    form.validateField(field.name);
                  }}
                  disablePast={dateProps?.disablePast ?? true}
                  format="MMM DD YYYY"
                  placeholder=""
                  size="small"
                  borderColor={hasError ? "var(--status-error)" : ""}
                  borderRadius="var(--radius-control)"
                  containerClassName={className || "w-100 px-2 pr-0 p-0 m-0"}
                />
                {hasError && (
                  <div className="text-danger px-2 mb-3 fs-12 mt-2">
                    {form.errors[field.name]}
                  </div>
                )}
              </>
            );
          }}
        </Field>
      );

    case "text":
      return (
        <>
          <StyledInputLabel shrink margin="dense" required={props?.required}>
            {label}
          </StyledInputLabel>
          <Field name={name}>
            {(fieldProps) =>
              textMDInputFormik({
                row: 4,
                size: "small",
                formGroupClass: inputWidthSx,
                ...fieldProps,
                ...props,
                className: "w-100 px-2 pr-0 p-0 mt-1 mb-0",
              })
            }
          </Field>
        </>
      );

    case "email":
      return (
        <Field name={name}>
          {(fieldProps) =>
            emailMDInputFormik({
              label,
              ...fieldProps,
              ...props,
            })
          }
        </Field>
      );

    case "number":
      return (
        <>
          <StyledInputLabel shrink margin="dense" required={props?.required}>
            {label}
          </StyledInputLabel>
          <Field name={name}>
            {(fieldProps) =>
              numberMDInputFormik({
                type: "number",
                size: "small",
                className: inputWidthSx,
                ...fieldProps,
                ...props,
              })
            }
          </Field>
        </>
      );

    case "radio":
      return (
        <Field name={name}>
          {({ field, meta }) => (
            <FormGroup>
              <FormLabel required={props?.required}>{label}</FormLabel>
              {selectOptions.map((option) => (
                <FormCheck
                  key={option.value}
                  type="radio"
                  label={option.label}
                  value={option.value}
                  checked={field.value === option.value}
                  onChange={() =>
                    field.onChange({
                      target: { name: field.name, value: option.value },
                    })
                  }
                  isInvalid={meta.touched && meta.error}
                />
              ))}
              <FormFeedback type="invalid">{meta.error}</FormFeedback>
            </FormGroup>
          )}
        </Field>
      );

    case "dateRange":
      return (
        <Field
          name={name}
          validate={(value) => {
            const isFlightDatesField =
              name === "flight_dates" || name.startsWith("flight_dates[");
            if (isFlightDatesField) return undefined;
            if (
              props?.required &&
              (!value || !value.startDate || !value.endDate)
            ) {
              return "Please select a valid start and end date.";
            }
            return undefined;
          }}
        >
          {({ field, form }) => {
            const hasError =
              form.touched[field.name] &&
              (form.errors[field.name]?.startDate ||
                form.errors[field.name]?.endDate);
            const isOptional =
              field.name === "flight_dates" ||
              field.name.includes("flight_dates");
            const today = new Date().toISOString().split("T")[0];
            return (
              <>
                <StyledInputLabel
                  shrink
                  margin="dense"
                  className="mb-2"
                  required={!isOptional ? true : props?.required}
                >
                  {label}
                </StyledInputLabel>
                <MUIDateRange
                  initialRange={isOptional ? today : form.values[field.name]}
                  onChangeDate={(dates) => {
                    form.setFieldValue(field.name, {
                      startDate: dates[0],
                      endDate: dates[1] ? dates[1] : dates[0],
                    });
                  }}
                  error={hasError}
                  isEndDateRequired={false}
                  helperText={
                    hasError && !isOptional
                      ? `Start: ${form.errors[field.name]?.startDate || ""}, End: ${
                          form.errors[field.name]?.endDate || ""
                        }`
                      : ""
                  }
                  sx={{
                    root: dateRangeInputSx,
                    input: dateRangeTextSx,
                    adornment: dateRangeIconSx,
                  }}
                  format="MM/DD/YYYY"
                  disablePast={props?.disablePast || false}
                  isOptional={isOptional}
                  className="w-100 px-2 pr-0 p-0 m-0 mb-2"
                  size="large"
                />
                {hasError && !isOptional && (
                  <div
                    className="text-danger mt-1"
                    style={{ fontSize: "12px" }}
                  >
                    Start: {form.errors[field.name]?.startDate}, End:{" "}
                    {form.errors[field.name]?.endDate}
                  </div>
                )}
              </>
            );
          }}
        </Field>
      );

    case "numberRange":
      return (
        <Field name={name}>
          {(fieldProps) =>
            NumberRangeMDInputFormik({
              label,
              ...fieldProps,
              ...props,
            })
          }
        </Field>
      );

    case "checkbox":
      return (
        <Field name={name}>
          {({ field, form }) => (
            <div className="w-100 px-2">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      form.setFieldValue(field.name, isChecked);
                      props.dependentFields?.forEach(
                        ({ field, clearValue, whenChecked }) => {
                          if (isChecked === whenChecked) {
                            form.setFieldValue(field, clearValue);
                          }
                        }
                      );
                    }}
                    size="small"
                    color="primary"
                    {...props}
                  />
                }
                label={label}
                className={`mb-2 pb-2 mr-2 ${props?.className || ""}`}
                labelPlacement="center"
              />
            </div>
          )}
        </Field>
      );

    case "fieldArray":
      return (
        <GenericFieldArray
          name={name}
          label={label}
          fieldType={props.fieldType}
          maxFields={props.maxFields}
          required={props.required}
          {...props}
        />
      );

    default:
      return null;
  }
};

export default FormField;
